import React, { forwardRef } from "react";
import { Pressable as RNPressable } from "react-native";
import type { WithLayoutProps } from "~/hoc/withLayoutProps";
import { styleConstants as C } from "~/style/styleConstants";
import type {
  PressableProps as RNPressableProps,
  View,
  ViewStyle,
} from "react-native";

// We couldn't use withLayoutProps for Pressable component because it accepts style prop as a function
// and withLayoutProps is not designed to handle functions as props.
// To solve this, we created a custom Pressable component which can handle style prop as a function.

type NewPressableProps = Omit<RNPressableProps, keyof WithLayoutProps> &
  WithLayoutProps;

export const Pressable = forwardRef<View, NewPressableProps>(
  (
    {
      borderRadiusSmall,
      borderRadiusMedium,
      borderRadiusLarge,

      paddingExtraSmall,
      paddingSmall,
      paddingMedium,
      paddingLarge,
      paddingExtraLarge,

      paddingHorizontalExtraSmall,
      paddingHorizontalSmall,
      paddingHorizontalMedium,
      paddingHorizontalLarge,
      paddingHorizontalExtraLarge,

      paddingVerticalExtraSmall,
      paddingVerticalSmall,
      paddingVerticalMedium,
      paddingVerticalLarge,
      paddingVerticalExtraLarge,

      centerContent,

      justifyContentCenter,
      justifyContentFlexStart,
      justifyContentFlexEnd,
      justifyContentSpaceBetween,
      justifyContentSpaceAround,
      justifyContentSpaceEvenly,

      alignItemsFlexStart,
      alignItemsFlexEnd,
      alignItemsCenter,
      alignItemsStretch,
      alignItemsBaseline,

      alignSelfFlexStart,
      alignSelfFlexEnd,
      alignSelfCenter,
      alignSelfStretch,
      alignSelfBaseline,

      flexDirectionRow,
      flexDirectionColumn,
      flexDirectionRowReverse,
      flexDirectionColumnReverse,

      absoluteTopLeft,
      absoluteTopLeftSmall,
      absoluteTopLeftMedium,
      absoluteTopLeftLarge,
      absoluteTopLeftExtraLarge,
      absoluteTopRight,
      absoluteTopRightSmall,
      absoluteTopRightMedium,
      absoluteTopRightLarge,
      absoluteTopRightExtraLarge,
      absoluteBottomLeft,
      absoluteBottomLeftSmall,
      absoluteBottomLeftMedium,
      absoluteBottomLeftLarge,
      absoluteBottomLeftExtraLarge,
      absoluteBottomRight,
      absoluteBottomRightSmall,
      absoluteBottomRightMedium,
      absoluteBottomRightLarge,
      absoluteBottomRightExtraLarge,

      colorBackgroundWhite,
      colorBackgroundLight,
      colorBackgroundLightDark,
      colorBackgroundLightDarker,
      colorBackgroundTheme,
      colorBackgroundDarkLighter,
      colorBackgroundDarkLight,
      colorBackgroundDark,
      colorBackgroundBlack,
      colorBackgroundDanger,
      colorBackgroundWarning,
      colorBackgroundSuccess,
      colorBackgroundGray,
      colorBackgroundDisabled,
      colorBackgroundBackdrop,
      colorBackgroundBackdropWhite,

      flex,
      flexWrap,
      aspectRatioOne,
      style: passThroughStyle,
      children,
      ...passThroughProps
    },
    ref,
  ) => {
    const style: ViewStyle = {};

    if (borderRadiusSmall) style.borderRadius = C.borderRadiusSmall;
    if (borderRadiusMedium) style.borderRadius = C.borderRadiusMedium;
    if (borderRadiusLarge) style.borderRadius = C.borderRadiusLarge;

    if (paddingExtraSmall) style.padding = C.spacingExtraSmall;
    if (paddingSmall) style.padding = C.spacingSmall;
    if (paddingMedium) style.padding = C.spacingMedium;
    if (paddingLarge) style.padding = C.spacingLarge;
    if (paddingExtraLarge) style.padding = C.spacingExtraLarge;

    if (paddingHorizontalExtraSmall)
      style.paddingHorizontal = C.spacingExtraSmall;
    if (paddingHorizontalSmall) style.paddingHorizontal = C.spacingSmall;
    if (paddingHorizontalMedium) style.paddingHorizontal = C.spacingMedium;
    if (paddingHorizontalLarge) style.paddingHorizontal = C.spacingLarge;
    if (paddingHorizontalExtraLarge)
      style.paddingHorizontal = C.spacingExtraLarge;

    if (paddingVerticalExtraSmall) style.paddingVertical = C.spacingExtraSmall;
    if (paddingVerticalSmall) style.paddingVertical = C.spacingSmall;
    if (paddingVerticalMedium) style.paddingVertical = C.spacingMedium;
    if (paddingVerticalLarge) style.paddingVertical = C.spacingLarge;
    if (paddingVerticalExtraLarge) style.paddingVertical = C.spacingExtraLarge;

    if (centerContent) {
      style.justifyContent = "center";
      style.alignItems = "center";
    }
    if (justifyContentCenter) style.justifyContent = "center";
    if (justifyContentFlexStart) style.justifyContent = "flex-start";
    if (justifyContentFlexEnd) style.justifyContent = "flex-end";
    if (justifyContentSpaceBetween) style.justifyContent = "space-between";
    if (justifyContentSpaceAround) style.justifyContent = "space-around";
    if (justifyContentSpaceEvenly) style.justifyContent = "space-evenly";

    if (alignItemsFlexStart) style.alignItems = "flex-start";
    if (alignItemsFlexEnd) style.alignItems = "flex-end";
    if (alignItemsCenter) style.alignItems = "center";
    if (alignItemsStretch) style.alignItems = "stretch";
    if (alignItemsBaseline) style.alignItems = "baseline";

    if (alignSelfFlexStart) style.alignSelf = "flex-start";
    if (alignSelfFlexEnd) style.alignSelf = "flex-end";
    if (alignSelfCenter) style.alignSelf = "center";
    if (alignSelfStretch) style.alignSelf = "stretch";
    if (alignSelfBaseline) style.alignSelf = "baseline";

    if (flexDirectionRow) style.flexDirection = "row";
    if (flexDirectionColumn) style.flexDirection = "column";
    if (flexDirectionRowReverse) style.flexDirection = "row-reverse";
    if (flexDirectionColumnReverse) style.flexDirection = "column-reverse";

    if (absoluteTopLeft) {
      style.position = "absolute";
      style.top = 0;
      style.left = 0;
    }
    if (absoluteTopLeftSmall) {
      style.position = "absolute";
      style.top = C.spacingSmall;
      style.left = C.spacingSmall;
    }
    if (absoluteTopLeftMedium) {
      style.position = "absolute";
      style.top = C.spacingMedium;
      style.left = C.spacingMedium;
    }
    if (absoluteTopLeftLarge) {
      style.position = "absolute";
      style.top = C.spacingLarge;
      style.left = C.spacingLarge;
    }
    if (absoluteTopLeftExtraLarge) {
      style.position = "absolute";
      style.top = C.spacingExtraLarge;
      style.left = C.spacingExtraLarge;
    }
    if (absoluteTopRight) {
      style.position = "absolute";
      style.top = 0;
      style.right = 0;
    }
    if (absoluteTopRightSmall) {
      style.position = "absolute";
      style.top = C.spacingSmall;
      style.right = C.spacingSmall;
    }
    if (absoluteTopRightMedium) {
      style.position = "absolute";
      style.top = C.spacingMedium;
      style.right = C.spacingMedium;
    }
    if (absoluteTopRightLarge) {
      style.position = "absolute";
      style.top = C.spacingLarge;
      style.right = C.spacingLarge;
    }
    if (absoluteTopRightExtraLarge) {
      style.position = "absolute";
      style.top = C.spacingExtraLarge;
      style.right = C.spacingExtraLarge;
    }
    if (absoluteBottomLeft) {
      style.position = "absolute";
      style.bottom = 0;
      style.left = 0;
    }
    if (absoluteBottomLeftSmall) {
      style.position = "absolute";
      style.bottom = C.spacingSmall;
      style.left = C.spacingSmall;
    }
    if (absoluteBottomLeftMedium) {
      style.position = "absolute";
      style.bottom = C.spacingMedium;
      style.left = C.spacingMedium;
    }
    if (absoluteBottomLeftLarge) {
      style.position = "absolute";
      style.bottom = C.spacingLarge;
      style.left = C.spacingLarge;
    }
    if (absoluteBottomLeftExtraLarge) {
      style.position = "absolute";
      style.bottom = C.spacingExtraLarge;
      style.left = C.spacingExtraLarge;
    }
    if (absoluteBottomRight) {
      style.position = "absolute";
      style.bottom = 0;
      style.right = 0;
    }
    if (absoluteBottomRightSmall) {
      style.position = "absolute";
      style.bottom = C.spacingSmall;
      style.right = C.spacingSmall;
    }
    if (absoluteBottomRightMedium) {
      style.position = "absolute";
      style.bottom = C.spacingMedium;
      style.right = C.spacingMedium;
    }
    if (absoluteBottomRightLarge) {
      style.position = "absolute";
      style.bottom = C.spacingLarge;
      style.right = C.spacingLarge;
    }
    if (absoluteBottomRightExtraLarge) {
      style.position = "absolute";
      style.bottom = C.spacingExtraLarge;
      style.right = C.spacingExtraLarge;
    }

    if (colorBackgroundWhite) style.backgroundColor = C.colorBackgroundWhite;
    else if (colorBackgroundLight)
      style.backgroundColor = C.colorBackgroundLight;
    else if (colorBackgroundLightDark)
      style.backgroundColor = C.colorBackgroundLightDark;
    else if (colorBackgroundLightDarker)
      style.backgroundColor = C.colorBackgroundLightDarker;
    else if (colorBackgroundTheme)
      style.backgroundColor = C.colorBackgroundTheme;
    else if (colorBackgroundDarkLighter)
      style.backgroundColor = C.colorBackgroundDarkLighter;
    else if (colorBackgroundDarkLight)
      style.backgroundColor = C.colorBackgroundDarkLight;
    else if (colorBackgroundDark) style.backgroundColor = C.colorBackgroundDark;
    else if (colorBackgroundBlack)
      style.backgroundColor = C.colorBackgroundBlack;
    else if (colorBackgroundDanger)
      style.backgroundColor = C.colorBackgroundDanger;
    else if (colorBackgroundWarning)
      style.backgroundColor = C.colorBackgroundWarning;
    else if (colorBackgroundSuccess)
      style.backgroundColor = C.colorBackgroundSuccess;
    else if (colorBackgroundGray) style.backgroundColor = C.colorBackgroundGray;
    else if (colorBackgroundDisabled)
      style.backgroundColor = C.colorBackgroundDisabled;
    else if (colorBackgroundBackdrop)
      style.backgroundColor = C.colorBackgroundBackdrop;
    else if (colorBackgroundBackdropWhite)
      style.backgroundColor = C.colorBackgroundBackdropWhite;

    if (flex) style.flex = 1;

    if (flexWrap) style.flexWrap = "wrap";

    if (aspectRatioOne) {
      style.aspectRatio = 1;
    }

    return (
      <RNPressable
        ref={ref}
        style={({ pressed, hovered }) => [
          style,
          typeof passThroughStyle === "function"
            ? passThroughStyle({ pressed, hovered })
            : passThroughStyle,
        ]}
        {...passThroughProps}
      >
        {children}
      </RNPressable>
    );
  },
);

export type Pressable = View;
export type PressableProps = NewPressableProps;
